// emojiStrip.ts
/** Feature detect Unicode property escapes (TS/modern Chrome/Node support) */
const supportsUnicodeProps = (() => {
    try {
        // Extended_Pictographic is the workhorse for emoji
        new RegExp("\\p{Extended_Pictographic}", "u");
        return true;
    } catch {
        return false;
    }
})();

/**
 * Strip all Unicode emoji (incl. ZWJ sequences, flags, keycaps, VS, skin tones).
 * Leaves normal punctuation/letters intact and collapses extra whitespace.
 */
export function stripEmojis(input: string): string {
    if (!input) {
        return input;
    }

    if (supportsUnicodeProps) {
        // Property-based pattern (modern & robust)
        // - Base emoji: \p{Extended_Pictographic}
        // - Optional VS: FE0F/FE0E
        // - ZWJ chain: (\u200D \p{Extended_Pictographic} (VS)?)*
        // - Also remove Emoji Components (skin tones), regional indicators (flags), keycaps
        const emojiSeq =
            "(?:\\p{Extended_Pictographic}(?:\\uFE0F|\\uFE0E)?(?:\\u200D\\p{Extended_Pictographic}(?:\\uFE0F|\\uFE0E)?)*" +
            // plus standalone components we want gone as well
            "|[\\u{1F3FB}-\\u{1F3FF}]" + // skin tones
            "|[\\u{1F1E6}-\\u{1F1FF}]{2}" + // flags (regional indicators)
            "|[#*0-9]\\uFE0F?\\u20E3" + // keycap sequences (#️⃣, 0️⃣..9️⃣, *️⃣)
            ")";
        const emojiRe = new RegExp(emojiSeq, "gu");
        const vsAndZwj = /\u200D|\uFE0F|\uFE0E/gu; // just in case any strays remain
        return input
            .replace(emojiRe, "")
            .replace(vsAndZwj, "")
            .replace(/[ \t]+\n/g, "\n") // trim trailing spaces per line
            .replace(/[^\S\r\n]{2,}/g, " ") // collapse runs of spaces
            .trim();
    }

    // Fallback regex (older engines): covers BMP + common emoji planes/sequences
    const fallbackEmojiRe =
        /(?:[\u203C-\u3299]|\u00A9|\u00AE|[\u2194-\u21AA]|[\u231A-\u23FA]|[\u25AA-\u27BF]|[\u2B00-\u2BFF]|[\uD83C-\uDBFF][\uDC00-\uDFFF])|[#*0-9]\uFE0F?\u20E3/gu;

    return input
        .replace(fallbackEmojiRe, "")
        .replace(/\u200D|\uFE0F|\uFE0E/gu, "")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/[^\S\r\n]{2,}/g, " ")
        .trim();
}
